import { XIcon } from '@commander/ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { StepProps } from '../../types/onboarding.types';
import { SUGGESTIONS_MAP } from '../../utils/constants';
import Suggestions from '../shared/Suggestions';

const JobTitlesStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  isLoading,
  error,
}) => {
  const [input, setInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setLocalError(null);
  }, []);

  const handleAddJobTitle = (jobTitle: string) => {
    const trimmed = jobTitle.trim();
    if (!trimmed) {
      setLocalError('Please enter a job title');
      return;
    }

    if (data.jobTitles.includes(trimmed)) {
      setLocalError('This job title has already been added');
      return;
    }

    onChange({ jobTitles: [...data.jobTitles, trimmed] });
    setInput('');
    setLocalError(null);
  };

  const handleRemoveJobTitle = (jobTitle: string) => {
    const updated = data.jobTitles.filter((jt) => jt !== jobTitle);
    onChange({ jobTitles: updated });
    setLocalError(null);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setLocalError(null);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleAddJobTitle(suggestion);
    setLocalError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddJobTitle(input);
    }
  };

  const handleSubmit = async () => {
    const result = await validation(data);
    if (result.isValid) {
      setLocalError(null);
      onNext();
    } else {
      setLocalError(result.error ?? 'Please check your job titles and try again');
    }
  };

  return (
    <div className="space-y-6">
      {/* Suggestions */}
      <div className="mb-[28px]">
        <p className="flex items-center text-heading-4 font-paragraph mb-4 text-[#737982]">
          Suggestions
        </p>
        <Suggestions
          suggestions={SUGGESTIONS_MAP['Job Titles']}
          onSuggestionClick={handleSuggestionClick}
          layout="default"
        />
      </div>

      {/* Input field for direct entry */}
      <div className="relative w-full max-w-[508px]">
        <input
          id="job-titles-input"
          type="text"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Add job titles"
          aria-label="Add job titles"
          aria-describedby="job-titles-description"
          className="w-full max-w-[508px] text-body-text-large font-paragraph border border-border-gray rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 px-4 py-2 text-[#737982]"
        />
        <span id="job-titles-description" className="sr-only">
          Enter job titles to target in your search. Press enter to add.
        </span>
      </div>

      {/* Added Job Titles Display */}
      {data.jobTitles.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Target Job Titles ({data.jobTitles.length})
          </h3>
          <div className="flex flex-wrap gap-2 max-h-[250px] overflow-y-auto font-paragraph text-heading-4">
            {data.jobTitles.map((jobTitle) => (
              <div key={jobTitle} className="flex items-center m-1">
                <div className="bg-primary text-white rounded-l-[4px] px-3 py-1 flex items-center">
                  {jobTitle}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveJobTitle(jobTitle)}
                  aria-label={`Remove job title ${jobTitle}`}
                  className="bg-primary text-white rounded-r-md ml-[2px] px-[4px] py-1 flex items-center hover:bg-red-700 transition-colors"
                >
                  <XIcon className="w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || localError}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || data.jobTitles.length === 0}
        />
      </div>
    </div>
  );
};

export default JobTitlesStep;
